import {
	allowedTarget,
	type BrowserTask,
	contentFormats,
	fieldLength,
	fieldRequired,
	formatKey,
	type PlatformContract,
	type PublishPayload,
	validatePayload,
} from "@ondit/publishing";
import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	ChipSwitch,
	Input,
	Label,
} from "@ondit/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArticleSheet } from "~/common/article/writing-field";
import { BrandMediaEditor, useBrandMedia } from "~/common/media/brand-media";
import { PublishingField } from "~/common/publishing/publishing-field";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import { useAccess } from "~/core/access/index";
import type { DistributionSource } from "~/core/api/distribution";
import { ApiError, api } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { articleMarkdown, shortPost } from "~/modules/analytics/lib/distribution";
import { ConnectAccount } from "./connect";
import { PublishConfirm } from "./publish-confirm";
import { ResultLink } from "./result-link";
import "./publishing.css";

export function PlatformComposer({
	source,
	platform,
	entityId,
	onStageChange,
	taskId,
	expandedPreview = false,
	readOnly = false,
	viewOnly = false,
	initialTask,
	seed,
	onFormatChange,
	saveDraft,
}: {
	source: DistributionSource;
	platform: PlatformContract;
	entityId: string;
	onStageChange?: (finishing: boolean) => void;
	taskId?: string;
	expandedPreview?: boolean;
	readOnly?: boolean;
	viewOnly?: boolean;
	initialTask?: BrowserTask;
	/** A version already written for this platform, which replaces the text drawn from the article. */
	seed?: Pick<PublishPayload, "fields" | "media" | "target_url">;
	/** Offered when the platform takes several kinds of post; `platform` is the one chosen. */
	onFormatChange?: (format: string) => void;
	/** For an article not yet live: save the post as its version, since there is nothing to send it to. */
	saveDraft?: (payload: PublishPayload) => Promise<void>;
}) {
	const brandMedia = useBrandMedia(entityId);
	const recoveryKey = `ondit.publisher.${entityId}.${source.id}.${source.revision}.${platform.id}${platform.format ? `.${platform.format}` : ""}`;
	const t = useT();
	const queryClient = useQueryClient();
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		refetchInterval: 5000,
	});
	const initialized = useRef(false);
	const observedTask = useRef("");
	const [handedOff, setHandedOff] = useState(false);
	useEffect(() => onStageChange?.(handedOff), [handedOff, onStageChange]);
	const [error, setError] = useState("");
	const [pane, setPane] = useState("edit");
	const [touched, setTouched] = useState<Set<string>>(new Set());
	const { locale } = useI18n();
	const { editor: canEdit } = useAccess();
	const editor = canEdit && !readOnly;
	const recoveredDraft = useRef(false);
	const [payload, setPayload] = useState<PublishPayload>(() => {
		if (initialTask) return initialTask.payload;
		for (const getStorage of [() => sessionStorage, () => localStorage]) {
			try {
				const cached = JSON.parse(getStorage().getItem(recoveryKey) ?? "null");
				if (
					cached?.platform === platform.id &&
					formatKey(platform, cached?.format) === (platform.format ?? "") &&
					cached?.source?.revision === source.revision &&
					cached?.fields &&
					Array.isArray(cached.media)
				) {
					recoveredDraft.current = true;
					return cached;
				}
			} catch {}
		}
		const titleField = platform.preview.fields.find((f) => f.key === "title");
		const post = shortPost(
			source,
			titleField,
			platform.preview.fields.find((f) => f.key === "body"),
		);
		const body = ["article", "newsletter", "discussion"].includes(platform.preview.layout)
			? articleMarkdown(source).replace(/^# [^\n]*\n\n/, "")
			: post.body;
		const fields: Record<string, string> = {};
		for (const f of platform.preview.fields)
			fields[f.key] =
				f.key === "body"
					? body
					: f.key === "title"
						? post.title
						: f.key === "canonical"
							? source.canonical
							: f.key === "status"
								? "draft"
								: "";
		return {
			version: 1,
			platform: platform.id,
			...(platform.format ? { format: platform.format } : {}),
			fields: { ...fields, ...seed?.fields },
			media: seed?.media ?? [],
			source: {
				page_id: source.id,
				revision: source.revision,
				canonical: source.canonical,
			},
			target_url: seed?.target_url || platform.entry_url,
		};
	});
	useEffect(() => {
		try {
			sessionStorage.setItem(recoveryKey, JSON.stringify(payload));
		} catch {}
	}, [recoveryKey, payload]);
	const [task, setTask] = useState<BrowserTask | null>(initialTask ?? null);
	const contentLocked = !!task && !["ready", "filled", "failed", "cancelled"].includes(task.state);
	const [mediaBusy, setMediaBusy] = useState(false);
	const [actionBusy, setBusy] = useState(false);
	const busy =
		actionBusy || mediaBusy || (brandMedia.loading && payload.media.some((m) => m.url.startsWith("ondit-file:")));
	const [notice, setNotice] = useState("");
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [scheduleAt, setScheduleAt] = useState("");
	const [confirm, setConfirm] = useState(false);
	const [resultUrl, setResultUrl] = useState("");
	const apiPublish = platform.methods.includes("api");
	const browserPublish = false;

	const [credentialId, setCredentialId] = useState("");
	const accounts = (tasks.data?.accounts ?? []).filter((a) => a.provider_id === platform.credential_provider);
	const [recoveredRequest] = useState(() => {
		try {
			const cached = JSON.parse(sessionStorage.getItem(`${recoveryKey}.request`) ?? "null");
			if (typeof cached?.key === "string" && typeof cached?.snapshot === "string")
				return cached as { key: string; snapshot: string };
		} catch {}
		return { key: crypto.randomUUID(), snapshot: "" };
	});
	const request = useRef(recoveredRequest);
	const resolvedMedia = brandMedia.resolve(payload.media);
	const issues = validatePayload(
		{
			...payload,
			media: payload.media.map((m, i) =>
				m.url.startsWith("ondit-file:") && brandMedia.files.some((f) => f.id === m.url)
					? { ...m, url: `https://preview.invalid/asset.${resolvedMedia[i].type.startsWith("video/") ? "mp4" : "png"}` }
					: m,
			),
		},
		platform,
	).filter((issue) => !(issue === "media" && brandMedia.loading));
	const dirty =
		!task || ["failed", "cancelled"].includes(task.state) || JSON.stringify(task.payload) !== JSON.stringify(payload);
	useEffect(() => {
		const data = tasks.data;
		if (!data) return;
		if (!initialized.current && apiPublish) {
			const first = data.accounts.find((a) => a.provider_id === platform.credential_provider);
			if (first) {
				setCredentialId(first.id);
				// A WordPress account names the site the task is bound to. An
				// OAuth account names a person, and adopting that as the target
				// leaves a task whose address is a user id.
				if (!initialTask && first.url && allowedTarget(platform, first.url))
					setPayload((p) => ({ ...p, target_url: first.url as string }));
			}
		}
		const explicit = taskId ?? new URLSearchParams(location.search).get("task");
		let id = task?.id ?? explicit;
		try {
			id ??= sessionStorage.getItem(`${recoveryKey}.task`);
		} catch {}
		const old = data.tasks.find(
			(row) =>
				row.id === id &&
				row.payload.platform === platform.id &&
				formatKey(platform, row.payload.format) === (platform.format ?? "") &&
				row.payload.source.page_id === source.id &&
				(readOnly || row.payload.source.revision === source.revision),
		);
		if (old) {
			setTask(old);
			if (!initialized.current && explicit && !recoveredDraft.current) setPayload(old.payload);
			if (old.published_url) setResultUrl(old.published_url);
			const stateKey = `${old.id}:${old.state}`;
			if (
				!expandedPreview &&
				observedTask.current !== stateKey &&
				["filled", "user_reported", "published", "remote_draft", "unknown"].includes(old.state)
			)
				setHandedOff(true);
			observedTask.current = stateKey;
		}
		initialized.current = true;
	}, [
		tasks.data,
		initialTask,
		task?.id,
		platform,
		apiPublish,
		source.id,
		source.revision,
		recoveryKey,
		taskId,
		expandedPreview,
		readOnly,
	]);
	async function run(fn: () => Promise<void>) {
		setBusy(true);
		setError("");
		try {
			await fn();
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.localized(locale)
					: e instanceof Error && e.message === "publisher_unavailable"
						? t("publishFlow.connectionLost")
						: e instanceof Error && e.message === "login_required"
							? t("publishFlow.signedOut")
							: e instanceof Error && e.message === "task_changed"
								? t("publishFlow.taskChanged")
								: String(e),
			);
		} finally {
			setBusy(false);
		}
	}
	async function save() {
		if (!editor || contentLocked) return null;
		if (issues.length) {
			toast.error(t("publishing.platform.invalid"));
			return null;
		}
		const snapshot = JSON.stringify(payload);
		if (["failed", "cancelled"].includes(task?.state ?? "") || request.current.snapshot !== snapshot)
			request.current = { key: crypto.randomUUID(), snapshot };
		try {
			sessionStorage.setItem(`${recoveryKey}.request`, JSON.stringify(request.current));
		} catch {}
		const row = await api.createPlatformTask(entityId, request.current.key, payload);
		try {
			sessionStorage.setItem(`${recoveryKey}.task`, row.id);
		} catch {}
		setTask(row);
		setPayload(row.payload);
		try {
			localStorage.removeItem(recoveryKey);
		} catch {}
		void queryClient.invalidateQueries({ queryKey: [...keys.entity(entityId), "platform-tasks"] });
		setNotice(t("publishing.platform.saved"));
		return row;
	}
	const change = (field: string, value: string) =>
		setPayload((p) => ({
			...p,
			fields: { ...p.fields, [field]: value },
			...(field === "destination" && value ? { target_url: value } : {}),
		}));
	/** What is wrong with a field, in the words both the form and the article sheet show. */
	const problem = (f: PlatformContract["preview"]["fields"][number]) => {
		const value = payload.fields[f.key] ?? "";
		const length = fieldLength(f, value);
		const over = f.max_length !== null ? length - f.max_length : 0;
		return t(
			over > 0
				? "publishFlow.tooLong"
				: !value.trim()
					? "publishFlow.fieldRequired"
					: f.min_length !== undefined && length < f.min_length
						? "publishing.platform.tooShort"
						: "publishing.platform.invalidField",
			{ count: over > 0 ? over : (f.min_length ?? 0) },
		);
	};
	const renderField = (f: PlatformContract["preview"]["fields"][number]) => (
		<PublishingField
			key={f.key}
			field={f}
			fields={payload.fields}
			disabled={!editor || busy || contentLocked}
			onChange={(value) => change(f.key, value)}
			onBlur={() => setTouched((old) => new Set(old).add(f.key))}
			invalid={issues.includes(f.key) && touched.has(f.key)}
			feedback={problem(f)}
			imageControl={
				<BrandMediaEditor
					onBusyChange={setMediaBusy}
					media={brandMedia}
					value={payload.fields[f.key] ? [{ url: payload.fields[f.key], alt: "" }] : []}
					onChange={(media) => {
						change(f.key, media[0]?.url ?? "");
						setTouched((old) => new Set(old).add(f.key));
					}}
					disabled={!editor || busy || contentLocked}
					accept={f.accept ?? "image/*"}
					maxCount={1}
				/>
			}
		/>
	);
	const previewMedia = resolvedMedia.map((m) => ({
		url: m.url,
		alt: m.alt,
		kind: m.type.startsWith("video/") ? ("video" as const) : ("image" as const),
	}));
	const previewFiles = Object.fromEntries(brandMedia.files.map((f) => [f.id.slice(11), f.url]));
	const fieldsLocked = !editor || busy || contentLocked;
	const mediaRule = platform.preview.media;
	const mediaField = mediaRule && (
		<fieldset disabled={fieldsLocked} className="bare-fieldset">
			<div className="field publisher-media-field" data-publish-field="media">
				<div className="publisher-field-label">
					<Label>{pick(locale, ["Media files", "媒体文件"])}</Label>
					<Hint text={t("publishing.platform.mediaHelp")} />
				</div>
				<BrandMediaEditor
					onBusyChange={setMediaBusy}
					media={brandMedia}
					value={payload.media}
					onChange={(media) => setPayload((p) => ({ ...p, media }))}
					disabled={fieldsLocked}
					accept={mediaRule.accept}
					maxCount={mediaRule.multiple === false ? 1 : (mediaRule.max_count ?? 20)}
				/>
				<p className="hint">
					{[
						mediaRule.required && !payload.media.length && t("publishing.platform.mediaRequired"),
						mediaRule.min_count !== undefined && t("publishing.platform.mediaMin", { count: mediaRule.min_count }),
						mediaRule.video_alone && t("publishing.platform.mediaVideoAlone"),
						mediaRule.no_video && t("publishing.platform.mediaNoVideo"),
						pick(locale, ["Saved media becomes publicly accessible.", "保存后媒体文件将可公开访问。"]),
					]
						.filter(Boolean)
						.join(" · ")}
				</p>
				{issues.includes("media") && (
					<p className="publisher-error" role="alert">
						{t("publishing.platform.invalidField")}
					</p>
				)}
			</div>
		</fieldset>
	);
	const titleField = platform.preview.fields.find((f) => f.key === "title");
	const bodyField = platform.preview.fields.find((f) => f.key === "body");
	// A post's title and body are one page, drawn the way the Agent draws the
	// article itself — a short post as much as a long one, so there is one
	// editor for text in the product. Plain text keeps its lines and takes no
	// marks the platform would drop.
	const asArticle = bodyField?.kind === "textarea";
	const richBody = ["markdown", "rich_text"].includes(bodyField?.format ?? "");
	const sheetField = (f: PlatformContract["preview"]["fields"][number]) => ({
		key: f.key,
		label: `${pick(locale, f.label)}${fieldRequired(f, payload.fields) ? " *" : ""}`,
		value: payload.fields[f.key] ?? "",
		onChange: (v: string) => change(f.key, v),
		note:
			issues.includes(f.key) && touched.has(f.key) ? (
				<p className="publisher-error" role="alert">
					{problem(f)}
				</p>
			) : null,
	});
	const mainFields =
		asArticle && bodyField ? (
			<fieldset
				disabled={fieldsLocked}
				className="bare-fieldset"
				onBlur={(event) => {
					const key = (event.target as HTMLElement).closest<HTMLElement>("[data-publish-field]")?.dataset.publishField;
					if (key) setTouched((old) => new Set(old).add(key));
				}}
			>
				<ArticleSheet
					disabled={fieldsLocked}
					title={titleField && sheetField(titleField)}
					body={sheetField(bodyField)}
					plainBody={!richBody}
					actions={
						<div className="row-actions publisher-sheet-counts">
							{[titleField, bodyField].map((f) => {
								if (!f || f.max_length === null) return null;
								const length = fieldLength(f, payload.fields[f.key] ?? "");
								return (
									<span key={f.key} className={length > f.max_length ? "publisher-count warn" : "publisher-count"}>
										{pick(locale, f.label)} {length} / {f.max_length}
									</span>
								);
							})}
							<Hint
								text={t(
									bodyField.transfer === "guided" ? "publishing.platform.guided" : "publishing.platform.automatic",
								)}
							/>
						</div>
					}
				/>
			</fieldset>
		) : (
			<fieldset disabled={fieldsLocked} className="bare-fieldset platform-fields">
				{platform.preview.fields.filter((f) => ["title", "body"].includes(f.key)).map(renderField)}
			</fieldset>
		);
	// Every setting open, in the order the platform's own form asks for them.
	const settingFields = platform.preview.fields.filter((f) => !["title", "body"].includes(f.key));
	const settings = settingFields.length > 0 && (
		<section className="publisher-settings">
			<h3>{pick(locale, ["Distribution settings", "分发设置"])}</h3>
			<fieldset disabled={fieldsLocked} className="bare-fieldset publisher-settings-grid">
				{settingFields.map(renderField)}
			</fieldset>
		</section>
	);
	const account = apiPublish && (
		<section className="publisher-settings" data-publish-field="account">
			<h3>{t("publishing.platform.account")}</h3>
			{accounts.length > 0 && (
				<SearchSelect
					label={t("publishing.platform.account")}
					placeholder={t("publishing.platform.required")}
					searchLabel={t("publishing.platform.account")}
					emptyLabel={t("table.noMatch")}
					disabled={fieldsLocked}
					value={credentialId}
					onChange={(id) => {
						const url = accounts.find((a) => a.id === id)?.url;
						setCredentialId(id);
						if (url && allowedTarget(platform, url)) setPayload((p) => ({ ...p, target_url: url }));
					}}
					options={accounts.map((a) => ({
						value: a.id,
						label: `${a.name}${a.status === "connected" ? "" : ` · ${t("publishFlow.channelNeedsLogin")}`}`,
					}))}
				/>
			)}
			{credentialId && accounts.find((a) => a.id === credentialId)?.status !== "connected" && (
				<Button
					variant="ghost"
					disabled={fieldsLocked}
					onClick={() =>
						void run(async () => {
							await api.loginPubzuAccount(entityId, credentialId, crypto.randomUUID());
							setNotice(t("publishFlow.channelLoginHelp"));
							void tasks.refetch();
						})
					}
				>
					{t("publishFlow.channelLogin")}
				</Button>
			)}
			<ConnectAccount entityId={entityId} platform={platform} disabled={fieldsLocked} onCreated={setCredentialId} />
		</section>
	);

	if (viewOnly)
		return (
			<PlatformPreview
				platform={platform}
				payload={payload}
				resolvedMedia={previewMedia}
				resolvedFiles={previewFiles}
				externalHeading={expandedPreview}
			/>
		);

	const recorded = !!task && ["published", "user_reported"].includes(task.state);
	return (
		<div className={`stack publisher-editor${expandedPreview ? " publishing-content-editor-expanded" : ""}`}>
			{/* Reviewing is what the screen is for; only a change of state gets a line. */}
			{(recorded || handedOff) && (
				<div className="publisher-step-heading">
					<h3>
						{t(recorded ? "publishFlow.recorded" : "publishFlow.finish", { platform: platform.name })}
						<Hint
							text={t(
								apiPublish
									? browserPublish
										? "publishFlow.bothMethods"
										: "publishFlow.apiMethod"
									: "publishFlow.reviewHelp",
							)}
						/>
					</h3>
					{handedOff && (
						<Button variant="ghost" onClick={() => setHandedOff(false)}>
							{t("publishFlow.edit")}
						</Button>
					)}
				</div>
			)}
			{handedOff && (
				<section className="publisher-next">
					<p role="status">{t(`publishing.platform.state.${task?.state ?? "ready"}`)}</p>
					{!apiPublish && task?.state !== "user_reported" && (
						<ol className="hint">
							<li>{t("publishFlow.inAssistant")}</li>
							<li>{t("publishFlow.onPlatform")}</li>
							<li>{t("publishFlow.returnLink")}</li>
						</ol>
					)}
					{task?.published_url && (
						<a href={task.published_url} target="_blank" rel="noreferrer">
							{t("publishFlow.openResult")}
						</a>
					)}
				</section>
			)}
			<div className="publisher-body" hidden={handedOff}>
				<fieldset
					className="bare-fieldset publisher-pane-switch"
					aria-label={pick(locale, ["Editor view", "编辑视图"])}
				>
					<Button variant="ghost" aria-pressed={pane === "edit"} onClick={() => setPane("edit")}>
						{pick(locale, ["Edit", "编辑"])}
					</Button>
					<Button variant="ghost" aria-pressed={pane === "preview"} onClick={() => setPane("preview")}>
						{t("publishing.platform.preview", { platform: platform.name })}
					</Button>
				</fieldset>
				{/* One shape for every platform: the channel's own fields top to
				    bottom in the order it asks for them, and its frame beside them.
				    Too narrow for both, the preview folds behind the switch above. */}
				<div className="publisher-workspace" data-pane={pane}>
					<div className="publisher-edit-column">
						<h3 className="publisher-column-heading">{pick(locale, ["Content", "内容编辑"])}</h3>
						{/* Each kind of post is its own editor on the platform and its own task
						    here; switching keeps the other's draft, which is saved per kind. */}
						{onFormatChange && contentFormats(platform).length > 1 && (
							<div className="publisher-format">
								<span>{pick(locale, ["Post as", "发布形式"])}</span>
								<ChipSwitch
									aria-label={pick(locale, ["Post as", "发布形式"])}
									value={platform.format ?? ""}
									onChange={onFormatChange}
									options={contentFormats(platform).map((f) => ({ value: f.id, label: pick(locale, f.label) }))}
								/>
							</div>
						)}
						{platform.browser.instructions && <p role="note">{pick(locale, platform.browser.instructions)}</p>}
						{mediaField}
						{mainFields}
						{settings}
						{account}
					</div>
					<aside className="publisher-preview">
						<PlatformPreview
							platform={platform}
							payload={payload}
							resolvedMedia={previewMedia}
							resolvedFiles={previewFiles}
						/>
					</aside>
				</div>
			</div>

			<div className="publisher-footer">
				{(error || tasks.error) && (
					<p role="alert" className="publisher-error">
						{error || t("publishFlow.loadFailed")}
					</p>
				)}
				<div className="publisher-footer-actions">
					<Hint text={t("publishFlow.distributionHelp")} />
					{!handedOff && (
						<Button
							variant="default"
							disabled={!editor || busy || contentLocked || !dirty}
							onClick={() =>
								void run(async () => {
									if (saveDraft) await saveDraft(payload);
									else if (issues.length) {
										localStorage.setItem(recoveryKey, JSON.stringify(payload));
										setNotice(pick(locale, ["Draft saved in this browser", "草稿已保存在此浏览器"]));
									} else await save();
								})
							}
						>
							{t("publishing.platform.save")}
						</Button>
					)}
					{!saveDraft && (
						<>
							<Button
								variant="primary"
								disabled={
									!editor ||
									busy ||
									tasks.isPending ||
									!!tasks.error ||
									contentLocked ||
									issues.length > 0 ||
									!credentialId ||
									accounts.find((a) => a.id === credentialId)?.status !== "connected"
								}
								onClick={() => setConfirm(true)}
							>
								{t("publishing.platform.publish", { platform: platform.name })}
							</Button>
							<Button
								variant="ghost"
								disabled={
									!editor ||
									busy ||
									contentLocked ||
									issues.length > 0 ||
									!credentialId ||
									accounts.find((a) => a.id === credentialId)?.status !== "connected"
								}
								onClick={() => setScheduleOpen(true)}
							>
								{t("publishFlow.schedule")}
							</Button>
						</>
					)}
					{task?.state === "sending" && (
						<Button
							variant="ghost"
							disabled={!editor || busy}
							onClick={() =>
								void run(async () => {
									await api.reportPlatformTask(entityId, task.id, "cancelled");
									void tasks.refetch();
								})
							}
						>
							{t("common.cancel")}
						</Button>
					)}
					{task?.state === "unknown" && (
						<ResultLink
							title={source.title}
							value={resultUrl}
							disabled={!editor || busy}
							onSave={async (url) => {
								await api.reportPlatformTask(entityId, task.id, "user_reported", url);
								setResultUrl(url);
								void tasks.refetch();
							}}
						/>
					)}
				</div>
			</div>
			<ChipModal open={scheduleOpen} onOpenChange={setScheduleOpen} srTitle={t("publishFlow.schedule")}>
				<ChipModalHeader onClose={() => setScheduleOpen(false)} closeAriaLabel={t("common.close")}>
					{t("publishFlow.schedule")}
				</ChipModalHeader>
				<ChipModalBody>
					<ChipModalField type="custom" title={t("publishFlow.scheduleAt")}>
						<Input
							type="datetime-local"
							aria-label={t("publishFlow.scheduleAt")}
							value={scheduleAt}
							disabled={!editor || busy}
							onChange={(e) => setScheduleAt(e.target.value)}
						/>
					</ChipModalField>
					<p className="hint">{t("publishFlow.distributionHelp")}</p>
					{error && <p role="alert">{error}</p>}
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("common.cancel")}
					onCancel={() => setScheduleOpen(false)}
					primaryAction={{
						label: t("publishFlow.schedule"),
						disabled: !editor || busy || !scheduleAt || new Date(scheduleAt).getTime() <= Date.now(),
						onClick: () =>
							void run(async () => {
								const row = dirty ? await save() : task;
								if (!row) return;
								setTask(
									await api.schedulePubzuTask(entityId, row.id, credentialId, new Date(scheduleAt).toISOString()),
								);
								setScheduleOpen(false);
								void tasks.refetch();
							}),
					}}
				/>
			</ChipModal>
			{notice && <p role="status">{notice}</p>}
			<PublishConfirm
				open={confirm}
				onOpenChange={setConfirm}
				entityId={entityId}
				platform={platform}
				credentialId={credentialId}
				detail={<p>{t("publishFlow.distributionHelp")}</p>}
				resolveTask={async () => (dirty ? await save() : task)}
				onPublished={(row, result) => {
					setTask({ ...row, ...result });
					setHandedOff(true);
					void tasks.refetch();
				}}
			/>
		</div>
	);
}
